import axios, {
	type AxiosError,
	type AxiosResponse,
	isAxiosError,
} from "axios";
import { APIError, type APIErrorResponse } from "@/adapters/api/errors.ts";
import config from "@/config";
import { useAuth } from "@/store/use-auth";

export const client = axios.create({
	baseURL: config.api.baseURL,
});

client.interceptors.request.use((cfg) => {
	const { session } = useAuth.getState();
	if (session) {
		cfg.headers.Authorization = `Bearer ${session.token}`;
	}
	return cfg;
});

let isHandlingExpiredSession = false;

client.interceptors.response.use(
	(response) => response,
	(error) => {
		const status = isAxiosError(error) ? error.response?.status : undefined;
		const { session, signOut } = useAuth.getState();
		const inOnboarding = window.location.pathname.startsWith("/onboarding");
		if (status === 401 && session && !inOnboarding && !isHandlingExpiredSession) {
			isHandlingExpiredSession = true;
			signOut();
			if (window.location.pathname !== "/login") {
				window.location.replace("/login");
			}
		}
		return Promise.reject(error);
	},
);

export async function handleRequest<Resp>(
	f: () => Promise<Resp>,
): Promise<Resp> {
	try {
		return await f();
	} catch (err) {
		const axiosErr = err as AxiosError<APIErrorResponse>;
		if (isAxiosError(err) && axiosErr.response?.data) {
			throw new APIError(axiosErr.response.data);
		}
		throw err;
	}
}

export async function handleRequestBody<Resp>(
	f: () => Promise<AxiosResponse<Resp>>,
): Promise<Resp> {
	return handleRequest(async () => (await f()).data);
}
